/**
 * Service.App — central app lifecycle service (ESM, ha/440).
 *
 * Conversion namespace→ESM. Recette appliquée :
 *  1. Wrapper `namespace HA.Core.Service { … }` retiré.
 *  2. Aliases `import X = HA.Core.X` remplacés par stubs locaux pour les
 *     dépendances out-of-cluster (FSApp, AppRepository, HAApp, HANavigationApp,
 *     ExtWindow). Le test ha/440 du registre n'instancie pas Service.App ;
 *     ha/441 remplacera ces stubs par les vrais imports ESM.
 *  3. Refs implicites (`toMap`, `timeout`, `log`, `NOTIFICATION`, `getService`)
 *     → imports explicites depuis `HA`.
 *
 * Note : `processAction` lit `NOTIFICATION` via l'export du module HA. En
 * ESM les `let` exports sont live-bindings → la valeur posée par
 * `HA.setNotification(…)` au boot est visible ici sans intermédiaire global.
 */

import { Service } from "./Service";
import type { IWebPageService } from "./Service";
import { Tag } from "../HTML/Tag";
import { openSecretReveal } from "./SecretReveal";
import { FSApp } from "../Entity/FSApp";
import { ExtWindow as ExtWindowCtor } from "../HTML/Container/Window/ExtWindow";
import { App as AppRepository } from "../Repository/App";
import {
    toMap,
    timeout,
    log,
    NOTIFICATION,
    getService,
} from "../../../HA";

type AppEntity = any;
type HAApp = any;
type HANavigationApp = any;

/**
 * Service central du cycle de vie des applications HA.
 *
 * Responsabilités :
 *  - Créer et retrouver les App via AppRepository
 *  - Dispatcher les actions JSON (processAction) vers IWebPageService
 *  - Exposer les opérations FSApp (floating/modal windows)
 *
 * Clés HAObject :
 *  - "appRepository" : AppRepository (lazy init)
 *  - "ha.service.webpage" : IWebPageService (injecté via services)
 */
export class App extends Service {

    public static createFSApp(data: any): FSApp {
        return new FSApp(data);
    }

    public getService(id: string): unknown {
        return (this as unknown as { get(k: string): unknown }).get(id);
    }

    public getWebPageService(): IWebPageService {
        return (this as unknown as { get(k: string): IWebPageService }).get("ha.service.webpage");
    }

    /** Ouvre une URL externe dans une FSApp (floating window). */
    public showExtPage(url: string): string | number {
        const app = App.createFSApp(
            new ExtWindowCtor(toMap({ src: "/file/?redirect2=" + url })),
        );
        this.showFSApp(app);
        return app.getId();
    }

    public createAndShowFSApp(data: any): string | number {
        const app = App.createFSApp(data);
        this.showFSApp(app);
        return app.getId();
    }

    public showFSApp(app: FSApp): void {
        this.getWebPageService().showFSApp(app);
    }

    public closeFSApp(id: string): void {
        this.getWebPageService().removeApp(id);
    }

    public findById(data: Map<string, any>, isNavigation?: boolean): Promise<unknown> {
        data.delete("cb");
        return this.getAppRepository().findById(toMap(data), isNavigation);
    }

    public createApp(
        data: Map<any, any>,
        elementData: Map<any, any>,
        isNavigation?: boolean,
    ): AppEntity | HAApp | HANavigationApp | FSApp {
        return this.getAppRepository().createApp(data, elementData, isNavigation);
    }

    /**
     * Dispatche une action JSON vers le service de navigation.
     * (Vocabulaire d'actions inchangé du legacy — cf. ancien docblock.)
     */
    public processAction(data: Map<any, any>): void {
        const action = data.get("t") ?? data.get("todo");
        // Réponse de succès portant un `redirect` mais aucun type d'action
        // (`{ok:true, redirect:"/admin/…"}` — convention forms HorApp). Sans ce
        // garde elle tombait dans le switch sans case → `redirect` ignoré (cause
        // racine ha/512). On honore le contrat : naviguer vers la cible.
        if (!action && data.get("redirect")) {
            this.redirect(data.get("redirect"));
            return;
        }
        switch (action) {
            case "toggleApp":
                this.getWebPageService().toggleApp(data);
                break;
            case "showApp":
                this.getWebPageService().showApp(data);
                break;
            case "app":
            case "reloadApp":
                this.getWebPageService().reloadApp(data);
                break;
            case "rc":
            case "reloadCurrent":
                this.getWebPageService().reloadApp(null);
                break;
            case "showAppMenu":
                this.getWebPageService().showAppMenu(data);
                break;
            case "showModal":
            case "sm":
                data.set("_m", "modal");
                this.getWebPageService().showModal(data);
                break;
            case "rma":
                this.getWebPageService().removeApp(data.get("id"));
                break;
            case "rmc":
                this.getWebPageService().removeCurrentApp();
                break;
            case "rnav":
                this.getWebPageService().reloadNav();
                break;
            case "rp":
                this.getWebPageService().reloadPreviousApp();
                break;
            case "service":
                (getService(data.get("id")) as { process: (d: unknown) => void }).process(data);
                break;
            case "msg":
                log(data);
                break;
            case "reveal":
                // HA-252 : secret à affichage unique — fenêtre dédiée, JAMAIS log()/msg.
                openSecretReveal(data);
                break;
            case "bulk":
            case "steps":
                this.processBulkAction(data.get("steps"));
                break;
            case "notw":
                NOTIFICATION?.watch(data);
                break;
            case "idle":
                timeout(() => this.processAction(toMap(data.get("data")) as Map<any, any>), data.get("d"));
                break;
            case "url":
                timeout(() => window.open(data.get("url"), data.get("target")), 1000);
                break;
            case "redirect":
                this.redirect(data.get("url") ?? data.get("redirect"), data.get("target"));
                break;
            case "dom":
                this.updateDom(data);
                break;
            case "cc":
                this.getAppRepository().clearCache();
                break;
        }
    }

    /**
     * Navigue vers une URL issue d'une réponse serveur — soit le champ `redirect`
     * de la convention forms HorApp (`{ok, errors, message, redirect, …}`), soit
     * l'action native `t:"redirect"` (cf. RedirectAction, runtime-types).
     *
     * Navigation pleine page (`location.assign`) pour une cible interne : le
     * serveur SSR la page résolue (ex. fiche pro forma par référence humaine
     * ha/464 `/admin/agency/proforma?reference=PF-…`). Pas de showApp SPA ici :
     * un `redirect` ne transporte qu'une URL, pas le pageId — le résoudre côté
     * client supposerait un registre URL→pageId inexistant ; la résolution
     * serveur est la voie correcte et générale.
     *
     * `target:"_blank"` → nouvel onglet (ne quitte pas l'app courante).
     */
    protected redirect(url: string, target?: string): void {
        if (!url) { return; }
        if (target === "_blank") {
            window.open(url, target);
            return;
        }
        window.location.assign(url);
    }

    /**
     * Remplace le contenu d'un élément DOM. Modes (clé "doma") : "repl" vide la
     * cible et y injecte un nouveau Tag (sélection par "eid" ou "eq").
     */
    protected updateDom(data: Map<any, any>): void {
        if (data.get("doma") !== "repl") return;
        const t: Tag | null = data.has("eid")
            ? new Tag().findById(data.get("eid"), true)
            : new Tag().findByQuery(data.get("eq"));
        if (!t) return;
        t.clear();
        t.appendChild(new Tag(data.get("c")));
    }

    public removeApp(id: string): void {
        this.getAppRepository().remove(id);
    }

    protected processBulkAction(steps: any[]): void {
        for (const item of steps) {
            this.processAction(toMap(item) as Map<any, any>);
        }
    }

    public getAppRepository(): AppRepository {
        const self = this as unknown as { hasNot(k: string): boolean; addToData(k: string, v: unknown): unknown; get(k: string): unknown; getData(): Map<any, any> };
        if (self.hasNot("appRepository")) {
            self.addToData("appRepository", new AppRepository(toMap(self.getData())));
        }
        return self.get("appRepository") as AppRepository;
    }
}
